/**
 * Integrytas Group - Common Security Module (CSM)
 * WorkspaceBreadcrumb - the path bar at the top of the active workspace tab.
 *
 * Shows the ancestor chain of the active page (Home > Security > Users),
 * reconstructed from parent_page_id. Clicking a parent node opens a small,
 * low-radius dropdown of that node's child pages so the user can jump to a
 * sibling page without re-opening the main sidebar.
 *
 * Slate-and-steel styling: white bar, csm-border hairline, steel-blue active
 * crumb, sharp rounded-sm dropdown. All props use snake_case.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, ChevronRight, ChevronDown } from 'lucide-react';
import type { PageAccess } from '../../types/auth';
import { is_nav_group_code } from '../../lib/nav_groups';

export interface breadcrumb_navigate_target {
  page_internal_code: string;
  display_text: string;
  route_path: string;
}

export interface workspace_breadcrumb_props {
  active_code: string | null;
  menu: PageAccess[];
  page_labels: Record<string, string>;
  on_navigate: (target: breadcrumb_navigate_target) => void;
}

interface crumb_node {
  page_id: number;
  page_internal_code: string;
  label: string;
  route_path: string;
}

export function WorkspaceBreadcrumb({ active_code, menu, page_labels, on_navigate }: workspace_breadcrumb_props) {
  const { t } = useTranslation();
  const [open_id, set_open_id] = useState<number | null>(null);
  const bar_ref = useRef<HTMLDivElement | null>(null);

  const { by_id, by_code, children_of } = useMemo(() => {
    const by_id = new Map<number, PageAccess>();
    const by_code = new Map<string, PageAccess>();
    const children_of = new Map<number, PageAccess[]>();
    for (const p of menu) {
      by_id.set(p.page_id, p); by_code.set(p.page_internal_code, p);
      if (p.parent_page_id != null) {
        const list = children_of.get(p.parent_page_id) ?? [];
        list.push(p); children_of.set(p.parent_page_id, list);
      }
    }
    for (const list of children_of.values()) list.sort((a, b) => a.sort_order - b.sort_order);
    return { by_id, by_code, children_of };
  }, [menu]);

  // Ancestor chain root -> active.
  const chain = useMemo<crumb_node[]>(() => {
    if (!active_code) return [];
    const out: crumb_node[] = [];
    let cur = by_code.get(active_code) ?? null;
    const guard = new Set<number>();
    while (cur && !guard.has(cur.page_id)) {
      guard.add(cur.page_id);
      out.unshift({ page_id: cur.page_id, page_internal_code: cur.page_internal_code, label: page_labels[cur.page_internal_code] ?? cur.page_internal_code, route_path: cur.route_path });
      cur = cur.parent_page_id != null ? (by_id.get(cur.parent_page_id) ?? null) : null;
    }
    // Drop crumbs with no resolved label or a raw NAV.* macro-group code.
    return out.filter(crumb => crumb.label && !crumb.label.startsWith('NAV.'));
  }, [active_code, by_code, by_id, page_labels]);

  // Close the dropdown on outside-click or Esc.
  useEffect(() => {
    if (open_id == null) return;
    const on_click = (e: MouseEvent) => { if (bar_ref.current && !bar_ref.current.contains(e.target as Node)) set_open_id(null); };
    const on_key = (e: KeyboardEvent) => { if (e.key === 'Escape') set_open_id(null); };
    window.addEventListener('mousedown', on_click);
    window.addEventListener('keydown', on_key);
    return () => { window.removeEventListener('mousedown', on_click); window.removeEventListener('keydown', on_key); };
  }, [open_id]);

  if (chain.length === 0) return null;

  const go = (p: { page_internal_code: string; route_path: string }) => {
    set_open_id(null);
    on_navigate({ page_internal_code: p.page_internal_code, display_text: page_labels[p.page_internal_code] ?? p.page_internal_code, route_path: p.route_path });
  };

  // Determine which chain nodes are visible (non-NAV, have a route and label).
  // We keep ALL nodes in `chain` so `children_of` lookups work for sibling
  // dropdowns; we just skip rendering the label/link text for NAV group nodes.
  const is_nav_group = (node: crumb_node) =>
    is_nav_group_code(node.page_internal_code) || !node.route_path || !node.label;

  return (
    <div
      ref={bar_ref}
      // Stable hook for tests: the bar has no role of its own (a second
      // <nav> would collide with the menu's), and pinning to its height
      // class made the e2e locator break on a styling change.
      data-testid="breadcrumb-bar"
      className="relative z-[1] flex h-[34px] shrink-0 items-center gap-[7px] border-b border-csm-hair bg-white px-5 text-csm-sm"
      style={{ marginBottom: '-0.5px' }}
    >
      <Home className="h-[13px] w-[13px] shrink-0 text-csm-muted" aria-hidden />
      {/* csm-muted (not watermark) — the watermark gray fails AA on this bar */}
      <span className="text-[11.5px] text-csm-muted">{t('common.breadcrumb.home', 'Home')}</span>
      {chain.map((node, i) => {
        const kids = (children_of.get(node.page_id) ?? []);
        const is_last = i === chain.length - 1;
        const is_open = open_id === node.page_id;
        const hide_label = is_nav_group(node);

        // NAV group nodes: keep for dropdown data but render nothing visible.
        if (hide_label && kids.length === 0) return null;

        return (
          <span key={node.page_id} className="flex items-center gap-[7px]">
            {!hide_label && <ChevronRight className="h-3 w-3 shrink-0 text-csm-ink4" aria-hidden />}
            <span className="relative flex items-center">
              {!hide_label && (
                <button
                  type="button"
                  onClick={() => (node.route_path && !is_last ? go(node) : undefined)}
                  className={'rounded-csm-control px-1 py-0.5 text-[11.5px] ' + (is_last ? 'font-semibold text-csm-ink' : 'text-csm-muted hover:bg-csm-surface-alt hover:text-primary')}
                >{node.label}</button>
              )}
              {kids.length > 0 && (
                <button
                  type="button"
                  aria-label={t('common.breadcrumb.node_pages', '{{label}} pages', { label: node.label })}
                  onClick={() => set_open_id(is_open ? null : node.page_id)}
                  className="ml-0.5 flex h-5 w-4 items-center justify-center rounded-csm-control text-csm-ink4 hover:bg-csm-surface-alt hover:text-primary"
                ><ChevronDown className="h-3 w-3" /></button>
              )}
              {is_open && kids.length > 0 && (
                <div className="absolute left-0 top-[30px] z-50 min-w-[12rem] rounded-csm-card border border-csm-border bg-white py-1 elev-1 anim-pop">
                  {kids.map(child => {
                    const child_label = page_labels[child.page_internal_code] ?? child.page_internal_code;
                    const is_group = !child.route_path;
                    return is_group ? (
                      <div key={child.page_id} className="px-3 py-1 text-csm-2xs font-semibold uppercase tracking-wide text-csm-muted">{child_label}</div>
                    ) : (
                      <button
                        key={child.page_id}
                        type="button"
                        onClick={() => go(child)}
                        className={'block w-full px-3 py-1 text-left text-csm-sm hover:bg-csm-surface-alt ' + (child.page_internal_code === active_code ? 'font-semibold text-primary' : 'text-csm-ink')}
                      >{child_label}</button>
                    );
                  })}
                </div>
              )}
            </span>
          </span>
        );
      })}
    </div>
  );
}
